<template>
  <div class="container admin-gpx-team">
    <h1 class="hover" @click="$router.back()">← Voltar</h1>

    <div v-if="loading" class="muted">Carregando...</div>
    <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

    <div v-else-if="data">
      <div class="header-row">
        <div>
          <h2>
            {{ data.teamName }}
            <span v-if="data.teamReview === 'approved'" class="badge big ok">aprovado</span>
            <span v-else-if="data.teamReview === 'rejected'" class="badge big bad">rejeitado</span>
          </h2>
          <p class="sub">
            {{ data.competitionName }} · Equipe #{{ data.teamId }} ·
            {{ data.files.length }} arquivo(s) ·
            {{ data.isRelay ? 'revezamento' : 'individual' }}
          </p>
        </div>
      </div>

      <!-- Anti-cheat signals -->
      <section v-if="antiCheat" class="anti-cheat">
        <h3>Análise anti-trapaça</h3>
        <div class="ac-grid">
          <!-- Veículo / bike -->
          <div class="ac-card" :class="acSpeedCls">
            <div class="ac-head"><span class="ac-icon">🚗</span> Velocidade</div>
            <div class="ac-main">{{ antiCheat.worstSpeed ? fmt(antiCheat.worstSpeed.kmh) + ' km/h' : '—' }}</div>
            <div class="ac-sub">
              <template v-if="antiCheat.anySpeedFail">⚠ acima do limite — possível veículo/bike</template>
              <template v-else-if="antiCheat.worstSpeed">sustentada máx · dentro do limite</template>
              <template v-else>sem dados</template>
            </div>
            <div class="ac-note" v-if="antiCheat.worstSpeed">
              perna: {{ antiCheat.worstSpeed.leg }}<br />
              pico instantâneo {{ fmt(antiCheat.maxInstant) }} km/h <small>(pode ser ruído de GPS)</small>
            </div>
          </div>
          <!-- Pular trecho -->
          <div class="ac-card" :class="acCovCls">
            <div class="ac-head"><span class="ac-icon">🗺️</span> Cobertura do percurso</div>
            <div class="ac-main">{{ antiCheat.coveragePct != null ? antiCheat.coveragePct + '%' : '—' }}</div>
            <div class="ac-sub">
              <template v-if="antiCheat.missingKm != null && antiCheat.routeKm != null">
                faltam <strong>{{ antiCheat.missingKm.toFixed(1) }} km</strong> de {{ antiCheat.routeKm.toFixed(1) }} km
              </template>
              <template v-else>percurso oficial não cadastrado</template>
            </div>
            <div class="ac-note">
              {{ data.isRelay ? 'combinada (união das pernas)' : 'do atleta' }}<span v-if="antiCheat.minCov != null"> · mínimo {{ antiCheat.minCov }}%</span>
            </div>
          </div>
          <!-- Fora do horário -->
          <div class="ac-card" :class="acTimeCls">
            <div class="ac-head"><span class="ac-icon">⏰</span> Janela de horário</div>
            <div class="ac-main">
              <template v-if="antiCheat.anyTimeFail">fora</template>
              <template v-else-if="antiCheat.anyTimeKnown">dentro</template>
              <template v-else>n/d</template>
            </div>
            <div class="ac-sub" v-if="antiCheat.teamStart != null">
              largada {{ clock(antiCheat.teamStart) }}<span v-if="raceStartMs"> (tiro {{ clock(raceStartMs) }})</span> · chegada {{ clock(antiCheat.teamFinish) }}
            </div>
            <div class="ac-note" v-if="antiCheat.elapsedSec != null">
              tempo total {{ fmtDuration(antiCheat.elapsedSec) }}
              <template v-if="antiCheat.anyTimeFail"><br />⚠ perna(s) fora da janela</template>
            </div>
          </div>
        </div>
      </section>

      <!-- Race statistics (aggregated from the tracks) -->
      <section v-if="raceStats" class="race-stats">
        <h3>Estatísticas da prova <small class="muted">({{ raceStats.n }} track(s) processada(s))</small></h3>
        <div class="stats-grid">
          <div class="stat-group">
            <div class="stat-group-title">Total da prova</div>
            <div class="stat"><span>Distância registrada</span><strong>{{ fmt(raceStats.totalDistanceKm) }} km</strong></div>
            <div class="stat"><span>Tempo somado</span><strong>{{ fmtDuration(raceStats.totalDurationSec) }}</strong></div>
            <div class="stat"><span>Ganho de elevação</span><strong>{{ raceStats.totalElevationGainM }} m</strong></div>
            <div class="stat"><span>Vel. média geral</span><strong>{{ fmt(raceStats.overallAvgKmh) }} km/h</strong></div>
          </div>
          <div class="stat-group">
            <div class="stat-group-title">Desvios detectados</div>
            <div class="stat">
              <span>Distância fora da rota</span>
              <strong>≈ {{ fmt(raceStats.offRouteKm) }} km<small v-if="raceStats.offRoutePctOverall != null"> ({{ fmt(raceStats.offRoutePctOverall) }}%)</small></strong>
            </div>
            <div class="stat"><span>Desvio máx. da rota</span><strong>{{ raceStats.maxDeviationM }} m</strong></div>
            <div class="stat"><span>Gaps de GPS</span><strong>{{ raceStats.totalGaps }}</strong></div>
          </div>
        </div>
      </section>

      <!-- Combined map, per-km splits below -->
      <div class="map-block">
        <mapbox-track
          ref="teamMap"
          :reference-geo="data.reference && data.reference.route"
          :traces="traces"
          :km-markers="kmMarkers"
          :pace-markers="paceMarkers"
          :alert-segments="alertSegments"
          :gap-segments="gapSegments"
          :start-point="data.reference && data.reference.start_point"
          :finish-point="data.reference && data.reference.finish_point"
          :start-label="antiCheat && antiCheat.teamStart != null ? clock(antiCheat.teamStart) : null"
          :finish-label="antiCheat && antiCheat.teamFinish != null ? clock(antiCheat.teamFinish) : null"
        />
        <p v-if="!data.reference" class="muted small">Sem rota oficial cadastrada para esta prova.</p>
        <p v-else-if="!mergedSplits.length" class="muted small">
          Sem parciais por km — reprocesse os arquivos para gerar os horários por km.
        </p>

        <div v-if="splitRows.length" class="splits-below">
          <h4>Parciais por km <small class="muted">horário de passagem · ritmo do km</small></h4>
          <div class="splits-chips">
            <div v-for="r in splitRows" :key="r.km"
                 :class="['split-chip', { suspect: r.suspect, major: r.km % 5 === 0 }]"
                 :title="'decorrido ' + r.elapsed">
              <span class="sc-km">km {{ r.km }}</span>
              <span class="sc-time">{{ r.clock }}</span>
              <span class="sc-pace">{{ r.pace }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Team decision -->
      <section class="team-decision">
        <h3>Decisão da equipe</h3>
        <p class="small muted">A decisão vale para todos os arquivos da equipe. Aprovar aceita o conjunto mesmo com pernas marcadas como suspeitas.</p>
        <textarea v-model="reviewNote" placeholder="Observação (enviada à equipe se rejeitar)"></textarea>
        <div class="decision-actions">
          <button class="btn btn-success" :disabled="busy" @click="review('approved')">✓ Aprovar equipe</button>
          <button class="btn btn-danger" :disabled="busy" @click="review('rejected')">✗ Rejeitar equipe</button>
          <button class="btn btn-light" :disabled="busy || !data.teamReview" @click="review('clear')">Limpar decisão</button>
        </div>
        <p v-if="actionMsg" class="action-msg">{{ actionMsg }}</p>
      </section>

      <!-- Per-leg files with full detail -->
      <h3>Arquivos da equipe</h3>
      <div v-for="(f, i) in data.files" :key="f.id" class="file-card">
        <div class="file-head" @click="toggle(f)">
          <span class="swatch" :style="{ background: colorFor(i) }"></span>
          <strong class="fname">{{ f.original_filename }}</strong>
          <span :class="'badge ' + verdictClass(f.verdict)">{{ verdictLabel(f.verdict, f.status) }}</span>
          <span class="expand">{{ expanded[f.id] ? '▲' : '▼' }}</span>
        </div>

        <div class="file-summary" v-if="f.signals">
          <span>{{ fmt(f.signals.totalDistanceKm) }} km</span>
          <span>cobertura {{ f.signals.coveragePercent != null ? f.signals.coveragePercent + '%' : '—' }}</span>
          <span>vel.máx {{ fmt(f.signals.maxSustainedKmh) }}</span>
          <span v-if="f.signals.offRoutePercent > 10" class="warn">off-route {{ f.signals.offRoutePercent }}%</span>
        </div>

        <!-- Expanded full detail -->
        <div v-if="expanded[f.id] && f.signals" class="file-detail">
          <ul class="gates">
            <li :class="gateClass(f.signals.gates.coverageOk)">
              Cobertura da rota: <strong>{{ f.signals.coveragePercent }}%</strong>
              <small v-if="f.signals.isRelay" class="note">(perna — não conta no veredito)</small>
              <small v-else-if="f.signals.gates.coverageOk != null">{{ f.signals.gates.coverageOk ? '✓' : '✗' }}</small>
            </li>
            <li :class="gateClass(f.signals.gates.maxSpeedOk)">
              Vel. máx. sustentada: <strong>{{ fmt(f.signals.maxSustainedKmh) }} km/h</strong>
              <small v-if="f.signals.gates.maxSpeedOk != null">{{ f.signals.gates.maxSpeedOk ? '✓' : '✗' }}</small>
            </li>
            <li :class="gateClass(f.signals.gates.timeWindowOk)">
              Janela de tempo:
              <strong>{{ f.signals.gates.timeWindowOk == null ? 'n/d' : (f.signals.gates.timeWindowOk ? 'dentro' : 'fora') }}</strong>
              <small v-if="f.signals.gates.timeWindowOk != null">{{ f.signals.gates.timeWindowOk ? '✓' : '✗' }}</small>
            </li>
          </ul>

          <table class="info-table">
            <tr><td>Distância total</td><td>{{ fmt(f.signals.totalDistanceKm) }} km</td></tr>
            <tr><td>Duração</td><td>{{ fmtDuration(f.signals.totalDurationSec) }}</td></tr>
            <tr><td>Vel. média</td><td>{{ fmt(f.signals.avgSpeedKmh) }} km/h</td></tr>
            <tr><td>Vel. instantânea máx.</td><td>{{ fmt(f.signals.maxInstantSpeedKmh) }} km/h</td></tr>
            <tr><td>Ganho de elevação</td><td>{{ f.signals.elevationGainM }} m</td></tr>
            <tr><td>Desvio máx. da rota</td><td>{{ f.signals.maxDeviationM != null ? f.signals.maxDeviationM + ' m' : '—' }}</td></tr>
            <tr :class="{ 'warn-row': f.signals.offRoutePercent > 10 }">
              <td>Pontos fora da rota</td>
              <td>{{ f.signals.offRoutePercent != null ? f.signals.offRoutePercent + '% (' + f.signals.offRoutePointsCount + ')' : '—' }}</td>
            </tr>
            <tr><td>Gaps de GPS</td><td>{{ f.signals.gapsCount }}</td></tr>
          </table>

          <div class="leg-thresholds">
            <strong>Ajustar limites e reprocessar (só esta perna)</strong>
            <div class="thr-inputs">
              <label>Cob. mín. % <input type="number" v-model.number="overrides[f.id].minCoveragePercent" placeholder="herdado" /></label>
              <label>Vel. máx <input type="number" v-model.number="overrides[f.id].maxRunningKmh" placeholder="herdado" /></label>
              <label>Buffer m <input type="number" v-model.number="overrides[f.id].routeBufferM" placeholder="herdado" /></label>
            </div>
            <button class="btn btn-secondary sm" :disabled="busy" @click="saveLeg(f.id)">Salvar e reprocessar</button>
            <button class="btn btn-light sm" :disabled="busy" @click="reprocessLeg(f.id)">Reprocessar</button>
          </div>

          <div class="leg-links">
            <a v-if="f.downloadUrl" :href="f.downloadUrl" target="_blank" rel="noopener" class="link">⬇ Baixar GPX</a>
            <router-link :to="'/admin/gpx/' + f.id" class="link">abrir em tela cheia →</router-link>
            <button class="link danger" @click="deleteLeg(f.id)">excluir arquivo</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import MapboxTrack from '@/components/Map/mapbox-track.vue';

const PALETTE = ['#e6550d', '#3182bd', '#31a354', '#756bb1', '#e377c2', '#17becf', '#bcbd22', '#8c564b', '#d62728', '#1f77b4'];

// Great-circle distance (m) between two [lon, lat] pairs.
function haversineM(a, b) {
  const R = 6371008.8, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]), dLon = toRad(a[0] - b[0]) * -1;
  const la1 = toRad(a[1]), la2 = toRad(b[1]);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

export default {
  name: 'admin-gpx-team',
  components: { MapboxTrack },

  data() {
    return {
      loading: true, busy: false, error: null,
      data: null, expanded: {}, overrides: {},
      reviewNote: '', actionMsg: null,
    };
  },

  computed: {
    teamId() { return parseInt(this.$route.params.teamId); },
    competitionId() { return parseInt(this.$route.params.competitionId); },
    traces() {
      if (!this.data) return [];
      return this.data.files
        .filter((f) => f.geom)
        .map((f, i) => ({ geo: f.geom, color: this.colorFor(i), label: f.original_filename }));
    },
    // Merge per-km splits across the team's tracks. Each km comes from the
    // file that covers it; on conflict pick the earliest time (the runner who
    // reached that km first). Returns sorted array of { km, lon, lat, timeMs }.
    mergedSplits() {
      if (!this.data) return [];
      const byKm = {};
      for (const f of this.data.files) {
        const s = f.signals && f.signals.kmSplits;
        if (!Array.isArray(s)) continue;
        for (const sp of s) {
          if (!byKm[sp.km] || sp.timeMs < byKm[sp.km].timeMs) byKm[sp.km] = sp;
        }
      }
      return Object.values(byKm).sort((a, b) => a.km - b.km);
    },
    raceStartMs() { return this.data && this.data.raceStartMs; },
    kmMarkers() {
      return this.mergedSplits.map((sp) => ({
        km: sp.km, lon: sp.lon, lat: sp.lat,
        label: this.clock(sp.timeMs),
      }));
    },
    // Ritmo abaixo disso (min/km) é impossível correndo — vira alerta.
    // Deriva do limite de velocidade da prova (default 20 km/h → 3:00/km).
    suspectPaceMin() {
      const thr = (this.data && this.data.reference && this.data.reference.thresholds) || {};
      const kmh = Number(thr.maxRunningKmh) || 20;
      return 60 / kmh;
    },
    // Pace label for each km, placed at the midpoint between consecutive km
    // markers (only for adjacent km with valid times).
    paceMarkers() {
      const out = [];
      const s = this.mergedSplits;
      for (let i = 1; i < s.length; i++) {
        const a = s[i - 1], b = s[i];
        if (b.km - a.km !== 1) continue;
        if (a.timeMs == null || b.timeMs == null) continue;
        const minPerKm = (b.timeMs - a.timeMs) / 1000 / 60;
        if (!(minPerKm > 0) || minPerKm > 60) continue;
        out.push({
          lon: (a.lon + b.lon) / 2,
          lat: (a.lat + b.lat) / 2,
          label: this.fmtPaceShort(minPerKm) + '/km',
          suspect: minPerKm < this.suspectPaceMin,
        });
      }
      return out;
    },
    // Trechos entre km consecutivos com ritmo impossível — linha vermelha.
    alertSegments() {
      const out = [];
      const s = this.mergedSplits;
      for (let i = 1; i < s.length; i++) {
        const a = s[i - 1], b = s[i];
        if (b.km - a.km !== 1) continue;
        if (a.timeMs == null || b.timeMs == null) continue;
        const minPerKm = (b.timeMs - a.timeMs) / 1000 / 60;
        if (minPerKm > 0 && minPerKm < this.suspectPaceMin) {
          out.push({
            coords: [[a.lon, a.lat], [b.lon, b.lat]],
            label: '⚠ ' + this.fmtPaceShort(minPerKm) + '/km',
          });
        }
      }
      return out;
    },
    // Km pulados (buracos entre parciais) — tracejado "sem registro".
    gapSegments() {
      const out = [];
      const s = this.mergedSplits;
      for (let i = 1; i < s.length; i++) {
        const a = s[i - 1], b = s[i];
        if (b.km - a.km <= 1) continue;
        out.push({
          coords: [[a.lon, a.lat], [b.lon, b.lat]],
          label: 'sem registro (' + (b.km - a.km) + ' km)',
        });
      }
      return out;
    },
    splitRows() {
      const rows = [];
      let prev = null;
      for (const sp of this.mergedSplits) {
        let pace = null;
        if (prev && sp.timeMs != null && prev.timeMs != null) {
          const dkm = sp.km - prev.km;
          if (dkm > 0) {
            pace = (sp.timeMs - prev.timeMs) / 1000 / 60 / dkm; // min/km
            // Sobreposição de pernas inverte o tempo; km de atletas com
            // horários distantes (ex.: madrugada) geram "ritmos" de horas.
            if (!(pace > 0) || pace > 60) pace = null;
          }
        }
        rows.push({
          km: sp.km,
          clock: this.clock(sp.timeMs),
          elapsed: this.elapsed(sp.timeMs),
          pace: pace != null ? this.fmtPace(pace) : '—',
          suspect: pace != null && pace < this.suspectPaceMin,
        });
        prev = sp;
      }
      return rows;
    },
    // Whole-race statistics aggregated from the processed tracks.
    raceStats() {
      if (!this.data) return null;
      const s = this.data.files.map((f) => f.signals).filter(Boolean);
      if (!s.length) return null;
      const sum = (k) => s.reduce((a, x) => a + (Number(x[k]) || 0), 0);
      const totalDistanceKm = sum('totalDistanceKm');
      const totalDurationSec = sum('totalDurationSec');
      // Sinais de desvio agregados (mais úteis que médias por track num
      // revezamento): distância fora da rota, pior desvio e total de gaps.
      let offRouteKm = 0, maxDeviationM = 0, totalGaps = 0;
      for (const x of s) {
        const d = Number(x.totalDistanceKm) || 0;
        const orp = Number(x.offRoutePercent);
        if (isFinite(orp)) offRouteKm += (d * orp) / 100;
        const dev = Number(x.maxDeviationM);
        if (isFinite(dev) && dev > maxDeviationM) maxDeviationM = dev;
        const g = Number(x.gapsCount);
        if (isFinite(g)) totalGaps += g;
      }
      return {
        n: s.length,
        totalDistanceKm,
        totalDurationSec,
        totalElevationGainM: Math.round(sum('elevationGainM')),
        overallAvgKmh: totalDurationSec > 0 ? totalDistanceKm / (totalDurationSec / 3600) : null,
        offRouteKm,
        offRoutePctOverall: totalDistanceKm > 0 ? (offRouteKm / totalDistanceKm) * 100 : null,
        maxDeviationM,
        totalGaps,
      };
    },

    // Comprimento oficial do percurso (km), a partir da rota de referência.
    routeKm() {
      const geo = this.data && this.data.reference && this.data.reference.route;
      if (!geo || !geo.coordinates) return null;
      let coords = [];
      if (geo.type === 'LineString') coords = geo.coordinates;
      else if (geo.type === 'MultiLineString') coords = geo.coordinates.reduce((a, c) => a.concat(c), []);
      else return null;
      let m = 0;
      for (let i = 1; i < coords.length; i++) m += haversineM(coords[i - 1], coords[i]);
      return m / 1000;
    },

    // Três sinais anti-trapaça derivados dos dados já existentes:
    // velocidade (veículo), cobertura combinada (pular trecho), janela (horário).
    antiCheat() {
      if (!this.data) return null;
      const files = this.data.files.filter((f) => f.signals);
      if (!files.length) return null;

      // --- velocidade: pior sustentada entre as pernas + se alguma falhou o gate
      let worstSpeed = null, anySpeedFail = false, maxInstant = 0;
      for (const f of files) {
        const sus = Number(f.signals.maxSustainedKmh);
        if (isFinite(sus) && (!worstSpeed || sus > worstSpeed.kmh)) {
          worstSpeed = { kmh: sus, leg: f.original_filename };
        }
        if (f.signals.gates && f.signals.gates.maxSpeedOk === false) anySpeedFail = true;
        const inst = Number(f.signals.maxInstantSpeedKmh);
        if (isFinite(inst) && inst > maxInstant) maxInstant = inst;
      }

      // --- cobertura: combinada (revezamento) ou do atleta (individual)
      let coveragePct = null, coverageOk = null, minCov = null;
      if (this.data.relay) {
        coveragePct = this.data.relay.combinedCoveragePercent;
        coverageOk = this.data.relay.coverageOk;
        minCov = this.data.relay.minCoveragePercent;
      } else {
        const s0 = files[0].signals;
        coveragePct = s0.coveragePercent;
        coverageOk = s0.gates ? s0.gates.coverageOk : null;
        minCov = s0.thresholds ? s0.thresholds.minCoveragePercent : null;
      }
      const routeKm = this.routeKm;
      const missingKm = (routeKm != null && coveragePct != null)
        ? Math.max(0, routeKm * (1 - coveragePct / 100)) : null;

      // --- janela: início mais cedo / fim mais tarde entre as pernas + gate
      let teamStart = null, teamFinish = null, anyTimeFail = false, anyTimeKnown = false;
      for (const f of files) {
        const s = f.signals;
        const st = s.onRouteFirstTimestamp != null ? s.onRouteFirstTimestamp : s.firstTimestamp;
        const en = s.onRouteLastTimestamp != null ? s.onRouteLastTimestamp : s.lastTimestamp;
        if (st != null && (teamStart == null || st < teamStart)) teamStart = st;
        if (en != null && (teamFinish == null || en > teamFinish)) teamFinish = en;
        const tw = s.gates ? s.gates.timeWindowOk : null;
        if (tw != null) { anyTimeKnown = true; if (tw === false) anyTimeFail = true; }
      }
      // Tempo do time = do primeiro movimento à última chegada (dentro das
      // próprias tracks — consistente mesmo se a data do GPX divergir da largada).
      const elapsedSec = (teamFinish != null && teamStart != null && teamFinish >= teamStart)
        ? (teamFinish - teamStart) / 1000 : null;

      return {
        worstSpeed, anySpeedFail, maxInstant,
        coveragePct, coverageOk, minCov, routeKm, missingKm,
        teamStart, teamFinish, anyTimeFail, anyTimeKnown, elapsedSec,
      };
    },
    acSpeedCls() { return this.antiCheat && this.antiCheat.anySpeedFail ? 'bad' : (this.antiCheat && this.antiCheat.worstSpeed ? 'ok' : 'na'); },
    acCovCls() { const c = this.antiCheat && this.antiCheat.coverageOk; return c === false ? 'bad' : (c === true ? 'ok' : 'na'); },
    acTimeCls() { return this.antiCheat && this.antiCheat.anyTimeFail ? 'bad' : (this.antiCheat && this.antiCheat.anyTimeKnown ? 'ok' : 'na'); },
  },

  async created() {
    await this.load();
  },

  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await api.getJSONAsync('gpxAdminTeamGet', {
          teamId: this.teamId, competitionId: this.competitionId,
        });
        if (!res || res.error) { this.error = (res && res.message) || 'Erro ao carregar'; return; }
        this.data = res;
        this.reviewNote = res.teamReviewNote || '';
        // init per-leg override editors
        const ov = {};
        for (const f of res.files) {
          const o = f.signals && f.signals.thresholdsApplied ? {} : {};
          const cur = f.thresholds_override || {};
          ov[f.id] = {
            minCoveragePercent: cur.minCoveragePercent != null ? cur.minCoveragePercent : null,
            maxRunningKmh: cur.maxRunningKmh != null ? cur.maxRunningKmh : null,
            routeBufferM: cur.routeBufferM != null ? cur.routeBufferM : null,
          };
        }
        this.overrides = ov;
      } finally {
        this.loading = false;
      }
    },

    toggle(f) {
      const id = f && f.id != null ? f.id : f;
      this.$set(this.expanded, id, !this.expanded[id]);
      // Ao abrir o arquivo de um trecho, foca o mapa naquela perna.
      if (this.expanded[id] && f && f.geom && this.$refs.teamMap) {
        this.$refs.teamMap.focusTrace(f.geom);
      }
    },

    async review(action) {
      this.busy = true; this.actionMsg = null;
      try {
        const res = await api.postJSONAsync('gpxAdminTeamReview', {
          teamId: this.teamId, competitionId: this.competitionId,
          action, note: this.reviewNote || null,
        });
        if (res && res.error) throw new Error(res.message);
        this.actionMsg = action === 'rejected'
          ? 'Equipe rejeitada. E-mail enviado ao dono.'
          : (action === 'clear' ? 'Decisão limpa.' : 'Equipe aprovada.');
        await this.load();
      } catch (e) {
        this.actionMsg = 'Erro: ' + (e.message || e);
      } finally {
        this.busy = false;
      }
    },

    buildOverride(id) {
      const o = this.overrides[id] || {};
      const out = {};
      for (const k of Object.keys(o)) {
        if (o[k] != null && o[k] !== '' && isFinite(o[k])) out[k] = Number(o[k]);
      }
      return out;
    },
    async saveLeg(id) {
      this.busy = true; this.actionMsg = null;
      try {
        await api.postJSONAsync('gpxAdminSetThresholds', {
          gpxUploadId: id, thresholdsOverride: this.buildOverride(id), reprocess: true,
        });
        this.actionMsg = 'Limites salvos, reprocessando...';
        setTimeout(() => this.load(), 2500);
      } finally { this.busy = false; }
    },
    async reprocessLeg(id) {
      this.busy = true;
      try {
        await api.postJSONAsync('gpxAdminReprocess', { id });
        this.actionMsg = 'Reprocessando...';
        setTimeout(() => this.load(), 2500);
      } finally { this.busy = false; }
    },
    async deleteLeg(id) {
      if (!confirm('Excluir este arquivo? Será removido do R2 (metadados ficam para auditoria).')) return;
      this.busy = true;
      try {
        await api.deleteJSON('gpxAdminDelete', { id });
        await this.load();
      } finally { this.busy = false; }
    },

    clock(ms) {
      if (ms == null) return '—';
      const d = new Date(ms);
      const p = (n) => String(n).padStart(2, '0');
      return p(d.getHours()) + ':' + p(d.getMinutes()) + 'h';
    },
    elapsed(ms) {
      if (ms == null || this.raceStartMs == null) return '—';
      let s = Math.max(0, Math.round((ms - this.raceStartMs) / 1000));
      const h = Math.floor(s / 3600); s -= h * 3600;
      const m = Math.floor(s / 60);
      return (h > 0 ? h + 'h' : '') + String(m).padStart(h > 0 ? 2 : 1, '0') + 'min';
    },
    fmtPace(minPerKm) {
      if (!isFinite(minPerKm) || minPerKm <= 0) return '—';
      const m = Math.floor(minPerKm);
      const s = Math.round((minPerKm - m) * 60);
      return m + ':' + String(s).padStart(2, '0') + '/km';
    },
    fmtPaceShort(minPerKm) {
      const m = Math.floor(minPerKm);
      const s = Math.round((minPerKm - m) * 60);
      return m + ':' + String(s).padStart(2, '0');
    },
    colorFor(i) { return PALETTE[i % PALETTE.length]; },
    fmt(v) { return v != null ? Number(v).toFixed(2) : '—'; },
    fmtDuration(s) {
      if (s == null) return '—';
      const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
      return `${h}h ${m}min`;
    },
    gateClass(v) { return v == null ? 'gate-na' : (v ? 'gate-ok' : 'gate-fail'); },
    verdictLabel(v, status) {
      if (status && status !== 'processed') {
        return ({ uploaded: 'na fila', processing: 'processando', failed: 'falhou' })[status] || status;
      }
      return ({ ok: 'OK', warning: 'atenção', suspect: 'suspeito' })[v] || 'pendente';
    },
    verdictClass(v) { return ({ ok: 'ok', warning: 'wait', suspect: 'bad' })[v] || 'neutral'; },
  },
};
</script>

<style scoped lang="stylus">
.admin-gpx-team
  padding: 1.5em 0

.header-row
  display: flex
  justify-content: space-between
  align-items: flex-start
  gap: 1em
  margin-bottom: 1em
  flex-wrap: wrap

.sub
  color: #777
  margin: 0.2em 0 0

.relay-summary
  padding: 0.7em 1em
  border-radius: 8px
  font-size: 0.95em
  &.ok
    background: #d1e7dd
    color: #0f5132
  &.bad
    background: #f8d7da
    color: #842029
  strong
    font-size: 1.15em
    margin: 0 0.3em
  small
    display: block
    opacity: 0.8

.anti-cheat
  margin: 0 0 1.2em

  h3
    margin: 0 0 0.7em

.ac-grid
  display: grid
  grid-template-columns: repeat(3, 1fr)
  gap: 0.9em

  @media (max-width: 760px)
    grid-template-columns: 1fr

.ac-card
  padding: 0.9em 1em
  border-radius: 10px
  border: 1px solid rgba(0,0,0,0.1)
  background: #f7f9fa

  &.ok
    background: #e7f5ec
    border-color: rgba(15,81,50,0.25)
  &.bad
    background: #fdecec
    border-color: rgba(132,32,41,0.3)
  &.na
    background: rgba(0,0,0,0.03)

.ac-head
  font-size: 0.8em
  font-weight: 700
  text-transform: uppercase
  letter-spacing: 0.03em
  color: #555

.ac-icon
  font-size: 1.1em

.ac-main
  font-size: 1.7em
  font-weight: 800
  color: #2c3e50
  margin: 0.15em 0

  .ac-card.bad &
    color: #842029
  .ac-card.ok &
    color: #0f5132

.ac-sub
  font-size: 0.9em
  font-weight: 600
  color: #444

.ac-note
  margin-top: 0.5em
  font-size: 0.8em
  color: #777
  line-height: 1.4

.race-stats
  margin: 0 0 1.2em
  padding: 1em 1.2em
  border: 1px solid rgba(0,0,0,0.1)
  border-radius: 8px
  background: rgba(23,162,184,0.05)

  h3
    margin: 0 0 0.7em

.stats-grid
  display: grid
  grid-template-columns: 1fr 1fr
  gap: 1.5em

  @media (max-width: 700px)
    grid-template-columns: 1fr

.stat-group-title
  font-weight: 700
  color: #17a2b8
  font-size: 0.85em
  text-transform: uppercase
  letter-spacing: 0.03em
  margin-bottom: 0.4em

.stat
  display: flex
  justify-content: space-between
  padding: 0.3em 0
  border-bottom: 1px solid rgba(0,0,0,0.06)
  font-size: 0.9em

  span
    color: #666

.map-block
  margin-bottom: 1em

.splits-below
  margin-top: 0.9em

  h4
    margin: 0 0 0.5em

.splits-chips
  display: flex
  flex-wrap: wrap
  gap: 0.35em

.split-chip
  display: flex
  flex-direction: column
  align-items: center
  min-width: 64px
  padding: 0.3em 0.45em
  border: 1px solid rgba(0,0,0,0.1)
  border-radius: 6px
  background: #f7f9fa
  font-size: 0.72em
  line-height: 1.35

  .sc-km
    font-weight: 700
    color: #555

  .sc-time
    color: #333

  .sc-pace
    color: #17a2b8
    font-weight: 600

  &.major
    background: rgba(23,162,184,0.08)
    border-color: rgba(23,162,184,0.35)

  &.suspect
    background: #ff1744
    border-color: #c40000

    .sc-km, .sc-time, .sc-pace
      color: #fff

.team-decision
  margin: 1.2em 0
  padding: 1em 1.2em
  border: 1px solid rgba(0,0,0,0.12)
  border-radius: 8px
  background: #fafbfc

  h3
    margin: 0 0 0.4em

  textarea
    width: 100%
    min-height: 50px
    padding: 0.5em
    border: 1px solid #ccc
    border-radius: 4px
    margin: 0.4em 0

.decision-actions
  display: flex
  gap: 0.6em
  flex-wrap: wrap

.file-card
  border: 1px solid rgba(0,0,0,0.1)
  border-radius: 8px
  margin-bottom: 0.7em
  padding: 0.7em 0.9em

.file-head
  display: flex
  align-items: center
  gap: 0.5em
  cursor: pointer

.swatch
  width: 14px
  height: 14px
  border-radius: 3px
  flex: none

.fname
  flex: 1
  font-size: 0.92em

.expand
  color: #999
  font-size: 0.8em

.file-summary
  display: flex
  flex-wrap: wrap
  gap: 0.8em
  margin: 0.5em 0 0
  font-size: 0.82em
  color: #555

.file-summary .warn, .warn-row td
  color: #b8860b
  font-weight: 600

.file-detail
  margin-top: 0.8em
  border-top: 1px dashed rgba(0,0,0,0.12)
  padding-top: 0.8em

.gates
  list-style: none
  padding: 0
  margin: 0 0 0.8em
  li
    padding: 0.4em 0.6em
    border-radius: 6px
    margin-bottom: 0.3em
    font-size: 0.88em
    small
      float: right
      font-weight: 700
    .note
      float: none
      font-weight: 400
      color: #777
      font-size: 0.82em

.gate-ok
  background: #d1e7dd
.gate-fail
  background: #f8d7da
.gate-na
  background: rgba(0,0,0,0.04)

.info-table
  width: 100%
  border-collapse: collapse
  font-size: 0.85em
  td
    padding: 0.3em 0.5em
    border-bottom: 1px solid rgba(0,0,0,0.06)
  td:last-child
    text-align: right
    font-weight: 600

.leg-thresholds
  margin-top: 0.8em
  font-size: 0.85em

.thr-inputs
  display: flex
  gap: 0.8em
  flex-wrap: wrap
  margin: 0.4em 0

  label
    display: flex
    flex-direction: column
    font-size: 0.8em
    gap: 0.2em

  input
    width: 80px
    padding: 0.3em
    border: 1px solid #ccc
    border-radius: 4px

.leg-links
  display: flex
  gap: 1em
  align-items: center
  margin-top: 0.8em
  flex-wrap: wrap

.btn
  padding: 0.5em 1em
  border-radius: 5px
  border: none
  cursor: pointer
  font-size: 0.9em

.btn.sm
  padding: 0.35em 0.7em
  font-size: 0.82em
  margin-right: 0.4em

.btn-success
  background: #2ca25f
  color: #fff
.btn-danger
  background: #de2d26
  color: #fff
.btn-secondary
  background: #17a2b8
  color: #fff
.btn-light
  background: #eee

.badge
  display: inline-block
  padding: 0.15em 0.5em
  border-radius: 4px
  font-size: 0.8em
  &.big
    padding: 0.3em 0.7em
    font-size: 0.7em
    vertical-align: middle
.badge.ok
  background: #d1e7dd
  color: #0f5132
.badge.bad
  background: #f8d7da
  color: #842029
.badge.wait
  background: #fff3cd
  color: #664d03
.badge.neutral
  background: rgba(0,0,0,0.06)
  color: #555

.link
  color: #17a2b8
  text-decoration: none
  background: none
  border: none
  cursor: pointer
  font-size: 0.88em
  &.danger
    color: #c0392b

.action-msg
  margin-top: 0.8em
  padding: 0.5em
  background: rgba(23,162,184,0.1)
  border-radius: 5px
  font-size: 0.9em

.muted
  color: #999
.small
  font-size: 0.85em
.hover
  cursor: pointer
.alert
  padding: 0.8em 1em
  border-radius: 4px
.alert-danger
  background: #f8d7da
  color: #842029
</style>
